import { CSSProperties, ReactNode } from "react";
import { IntlFormatters } from "react-intl";

import {
  Event,
  FetchedData,
  WatchTime,
  WatchTimeByDevice,
  WatchTimeByRegion,
  DeviceType,
  UniqueUsersByDevice,
  UniqueUsersByRegion,
  PricingKind,
  PurchasePlan,
  Currency,
  OrderState,
  Order,
  BusinessInfo,
  FetchedList,
  LiveFileList,
  LiveFileStatus,
} from "../types";

import dateTimeFormat from "./dateTimeFormat";
import { isEventPreviewAvailable, isEventInVodProcessing } from "./eventHelpers";

export interface Pick {
  key: string;
  imgKey?: string;
  convertDateTime?: boolean;
  extraRender?: (rowData: any) => ReactNode | string;
  css?: CSSProperties[];
  txtCss?: (rowData: any) => CSSProperties[];
  contentCss?: (rowData: any) => CSSProperties[];
  [k: string]: any;
}

/* eslint-disable camelcase */
export type EventWithUIMeta = Event & {
  plan_ui: string;
  status_ui: string;
};

export type LiveFileWithUIMeta = {
  id: number; // integer
  name: string;
  cloudStorageId: string;
  titleId: string;
  startTime: number;
  endTime: number;
  autoTransfer: boolean;
  latestUpdate: number;
  status: string;
  rawStatus: LiveFileStatus;
  customerFileName: string;
  customerThumbnailName: string;
  isArchived: boolean;
};

interface Return {
  txt: string;
  imgUrl?: string;
  extraRender?: (rowData: any) => ReactNode | string;
  [k: string]: any;
}

export const objToArray = <T extends Record<string, any>>(obj: T, pick: Pick[] = [], intl?: IntlFormatters): Return[] =>
  pick.reduce((acc: Return[], { key, imgKey, convertDateTime, ...rest }, i) => {
    const val = obj[key];
    acc[i] = { txt: val || "--", ...rest };
    if (imgKey && obj[imgKey] !== undefined) acc[i].imgUrl = obj[imgKey];
    if (intl && convertDateTime && val) acc[i].txt = dateTimeFormat(val, intl);
    return acc;
  }, []);

const idMap: Record<string, any> = {
  MASSIVE: "meta.massive",
  MEDIUM: "meta.medium",
  SMALL: "meta.small",
  PREPARING: "meta.preparing",
  // Note: if data event is `before_start` but time til event schedule start is more than 12 hours, display preparing.
  BEFORE_START: "meta.preparing",
  PROCESSING: "meta.processing",
  // Note: map backend `server_open` status to `preview` status
  SERVER_OPEN: "meta.preview",
  LIVE: "meta.live",
  ENDED: "meta.ended",
  ENCODING: "meta.encoding",
  // Note: Status `CANCELING` and `ARCHIVING` is mainly used by backend logic and should not be displayed at CMS
  CANCELED: "meta.canceled",
  CANCELING: "meta.canceled",
  ARCHIVED: "meta.archived",
  ARCHIVING: "meta.archived",
  READY_FOR_VOD: "meta.ready-for-vod",
  VOD: "meta.vod",
};

const simuliveIdMap: Record<string, any> = {
  PREPARING: "meta.preparing",
  // Note: if data event is `before_start` but time til event schedule start is more than 12 hours, display preparing.
  BEFORE_START: "meta.preparing",
  PROCESSING: "meta.processing",
  // Note: display processing status for simulive before live status
  SERVER_OPEN: "meta.processing",
  LIVE: "meta.live",
  ENDED: "meta.ended",
  ENCODING: "meta.encoding",
  // Note: Status `CANCELING` and `ARCHIVING` is mainly used by backend logic and should not be displayed at CMS
  CANCELED: "meta.canceled",
  CANCELING: "meta.canceled",
  ARCHIVED: "meta.archived",
  ARCHIVING: "meta.archived",
  READY_FOR_VOD: "meta.ready-for-vod",
  VOD: "meta.vod",
};

export const addUIMeta = (data: Event, intl: IntlFormatters): EventWithUIMeta => {
  const { plan, status } = data;
  let statusId = (data.vod_to_live ? simuliveIdMap[status] : idMap[status]) || "NA";
  if (isEventPreviewAvailable(data)) {
    statusId = data.vod_to_live ? "meta.processing" : "meta.preview-available";
  } else if (isEventInVodProcessing(data)) {
    statusId = "meta.vod-processing";
  }
  return {
    ...data,
    plan_ui: intl.formatMessage({ id: idMap[plan] || "NA" }),
    status_ui: intl.formatMessage({
      id: statusId,
    }),
  };
};

export const event = (data: FetchedData<Event>, intl: IntlFormatters): any => ({
  ...data,
  data: addUIMeta(data.data, intl),
});

export const events = (data: FetchedList<Event>, intl: IntlFormatters): FetchedList<EventWithUIMeta> => ({
  ...data,
  data: data.data.map((d) => addUIMeta(d, intl)),
});

const statusMap = {
  [LiveFileStatus.Created]: "live-file.status.created",
  [LiveFileStatus.Transcoding]: "live-file.status.transcoding",
  [LiveFileStatus.TranscodeSucceeded]: "live-file.status.transcode-succeeded",
  [LiveFileStatus.TranscodeFailed]: "live-file.status.transcode-failed",
  [LiveFileStatus.Transferring]: "live-file.status.transferring",
  [LiveFileStatus.TransferSucceeded]: "live-file.status.transfer-succeeded",
  [LiveFileStatus.TransferFailed]: "live-file.status.transfer-failed",
} as const;

export const liveFileWithUIMeta = (data: LiveFileList, intl: IntlFormatters): LiveFileWithUIMeta => {
  const {
    id,
    name,
    cloud_storage_id,
    customer_file_id,
    start_time,
    end_time,
    auto_transfer,
    status_history,
    status,
    customer_file_name,
    customer_thumbnail_name,
    is_archived,
  } = data;
  const currentStatus =
    status_history.find((statusHistory) => statusHistory.status === status) ||
    status_history[status_history.length - 1];

  return {
    id,
    name,
    cloudStorageId: cloud_storage_id,
    titleId: customer_file_id,
    startTime: start_time / 1000,
    endTime: end_time / 1000,
    autoTransfer: auto_transfer,
    latestUpdate: currentStatus?.created_at ?? 0,
    status: intl.formatMessage({ id: statusMap[status] }),
    rawStatus: status,
    customerFileName: customer_file_name,
    customerThumbnailName: customer_thumbnail_name,
    isArchived: is_archived,
  };
};

export const liveFiles = (data: FetchedList<LiveFileList>, intl: IntlFormatters): FetchedList<LiveFileWithUIMeta> => ({
  ...data,
  data: data.data.map((d) => liveFileWithUIMeta(d, intl)),
});

const deviceMap = {
  [DeviceType.Pc]: "event-analytics-page.chart.device.pc",
  [DeviceType.AndroidTablet]: "event-analytics-page.chart.device.tablet-android",
  [DeviceType.IosMobileWeb]: "event-analytics-page.chart.device.mobile-web-ios",
  [DeviceType.SmartTv]: "event-analytics-page.chart.device.smart-tv",
  [DeviceType.AndroidTv]: "event-analytics-page.chart.device.android-tv",
  [DeviceType.IosApp]: "event-analytics-page.chart.device.ios-app",
  [DeviceType.AndroidMobileWeb]: "event-analytics-page.chart.device.mobile-web-android",
  [DeviceType.IosTablet]: "event-analytics-page.chart.device.tablet-ios",
  [DeviceType.AndroidApp]: "event-analytics-page.chart.device.android-app",
  [DeviceType.Others]: "event-analytics-page.chart.others",
  [DeviceType.Windows]: "event-analytics-page.chart.os.windows",
  [DeviceType.ChromeOs]: "event-analytics-page.chart.os.chrome-os",
  [DeviceType.MacOs]: "event-analytics-page.chart.os.mac-os",
  [DeviceType.Linux]: "event-analytics-page.chart.os.linux",
  [DeviceType.Android]: "event-analytics-page.chart.os.android",
  [DeviceType.Ios]: "event-analytics-page.chart.os.ios",
};

export interface WatchTimeData {
  label: string;
  watchTime: number;
}

export const watchTimeByDevice = (
  data: FetchedData<WatchTimeByDevice[] | null>,
  intl: IntlFormatters
): FetchedData<WatchTimeData[] | null> => ({
  ...data,
  data:
    data.data?.map((d) => ({
      ...d,
      label: deviceMap[d.device] ? intl.formatMessage({ id: deviceMap[d.device] }) : "- -",
      watchTime: d.watch_time,
    })) ?? null,
});

export const watchTimeByRegion = (
  data: FetchedData<WatchTimeByRegion[] | null>
): FetchedData<WatchTimeData[] | null> => ({
  ...data,
  data:
    data.data?.map((d) => ({
      ...d,
      label: d.region,
      watchTime: d.watch_time,
    })) ?? null,
});

export interface WatchTimeWithUIMeta {
  devices: WatchTimeData[] | null;
  regions: WatchTimeData[] | null;
  userCountDeciles: number[] | null;
  watchTimeAverage: number;
  updatedAt: number;
}

export const watchTime = (data: FetchedData<WatchTime>, intl: IntlFormatters): FetchedData<WatchTimeWithUIMeta> => ({
  ...data,
  data: {
    ...data.data,
    devices:
      data.data.devices?.map((d) => ({
        ...d,
        label: deviceMap[d.device] ? intl.formatMessage({ id: deviceMap[d.device] }) : "- -",
        watchTime: d.watch_time,
      })) ?? null,
    regions:
      data.data.regions?.map((d) => ({
        ...d,
        label: d.region,
        watchTime: d.watch_time,
      })) ?? null,
    userCountDeciles: data.data.user_percents
      ? Array.from(Array(10).keys()).map((index) => {
          if (data.data.user_percents) {
            const userPercentIndex = data.data.user_percents.findIndex(
              (element) => element.percent === `${index * 10}`
            );
            return data.data.user_percents[userPercentIndex]?.count || 0;
          }
          return 0;
        })
      : null,
    watchTimeAverage: data.data.watch_time_average,
    updatedAt: data.data.updated_at,
  },
});

export interface UniqueUserData {
  label: string;
  count: number;
}

export interface UniqueUsersWithUIMeta {
  all: UniqueUserData[] | null;
  live: UniqueUserData[] | null;
  vod: UniqueUserData[] | null;
  liveReportUpdatedAt: number;
  vodReportUpdatedAt: number;
}

export interface peakUserCount {
  peak_user_count: number;
}

export const uniqueUsersByDevice = (
  data: FetchedData<UniqueUsersByDevice>,
  intl: IntlFormatters
): FetchedData<UniqueUsersWithUIMeta> => ({
  ...data,
  data: {
    ...data.data,
    liveReportUpdatedAt: data.data.live_report_updated_at,
    vodReportUpdatedAt: data.data.vod_report_updated_at,
    all:
      data.data.all?.map((d) => ({
        ...d,
        label: deviceMap[d.device] ? intl.formatMessage({ id: deviceMap[d.device] }) : "- -",
      })) ?? null,
    live:
      data.data.live?.map((d) => ({
        ...d,
        label: deviceMap[d.device] ? intl.formatMessage({ id: deviceMap[d.device] }) : "- -",
      })) ?? null,
    vod:
      data.data.vod?.map((d) => ({
        ...d,
        label: deviceMap[d.device] ? intl.formatMessage({ id: deviceMap[d.device] }) : "- -",
      })) ?? null,
  },
});

export const uniqueUsersByRegion = (data: FetchedData<UniqueUsersByRegion>): FetchedData<UniqueUsersWithUIMeta> => ({
  ...data,
  data: {
    ...data.data,
    liveReportUpdatedAt: data.data.live_report_updated_at,
    vodReportUpdatedAt: data.data.vod_report_updated_at,
    all: data.data.all?.map((d) => ({ ...d, label: d.region })) ?? null,
    live: data.data.live?.map((d) => ({ ...d, label: d.region })) ?? null,
    vod: data.data.vod?.map((d) => ({ ...d, label: d.region })) ?? null,
  },
});

export const durationFormat = (duration: null | { hours: number; minutes: number }, intl: IntlFormatters): string => {
  if (!duration) return "- -";

  const { hours, minutes } = duration;
  const hoursText = Math.abs(hours) > 1 ? intl.formatMessage({ id: "hours" }) : intl.formatMessage({ id: "hour" });
  const minsText = Math.abs(minutes) > 1 ? intl.formatMessage({ id: "mins" }) : intl.formatMessage({ id: "min" });

  if (hours === 0) {
    return `${minutes} ${minsText}`;
  }
  if (hours > 0 && minutes === 0) {
    return `${hours} ${hoursText}`;
  }
  return `${hours} ${hoursText} ${minutes} ${minsText}`;
};

export const pricingKindDetailsMap: Record<PricingKind, Array<{ label: string; texts: Array<string> }>> = {
  [PricingKind.BASIC]: [
    {
      label: "upsell.plan.detail.live-streaming-event",
      texts: [
        "upsell.plan.detail.event-type",
        "upsell.plan.detail.peak-user",
        "upsell.plan.detail.resolution",
        "upsell.plan.detail.live-to-vod",
        "upsell.plan.detail.event-site",
      ],
    },
    {
      label: "upsell.plan.detail.security-solution",
      texts: ["upsell.plan.detail.drm"],
    },
  ],
  [PricingKind.STANDARD]: [
    {
      label: "upsell.plan.detail.live-streaming-event",
      texts: [
        "upsell.plan.detail.event-type",
        "upsell.plan.detail.peak-user",
        "upsell.plan.detail.resolution",
        "upsell.plan.detail.live-to-vod",
        "upsell.plan.detail.event-site",
      ],
    },
    {
      label: "upsell.plan.detail.security-solution",
      texts: ["upsell.plan.detail.drm"],
    },
  ],
  [PricingKind.PREMIUM]: [
    {
      label: "upsell.plan.detail.live-streaming-event.premium",
      texts: [
        "upsell.plan.detail.event-type.premium",
        "upsell.plan.detail.peak-user.premium",
        "upsell.plan.detail.resolution.premium",
        "upsell.plan.detail.live-to-vod",
        "upsell.plan.detail.event-site",
      ],
    },
    {
      label: "upsell.plan.detail.security-solution",
      texts: ["upsell.plan.detail.drm", "upsell.plan.detail.watermark"],
    },
    {
      label: "upsell.plan.detail.support",
      texts: ["upsell.plan.detail.api-and-sdk-integration", "upsell.plan.detail.operation-support"],
    },
  ],
};

export interface PurchasePlanWithUIMeta {
  id: string;
  kind: PricingKind;
  currency: Currency;
  amount: number;
  offeredSecond: number;
  promotionExpiredAt: number;
  duration: number;
  purchasable: boolean;
}

export const purchasePlans = (data: FetchedData<PurchasePlan[]>): FetchedData<PurchasePlanWithUIMeta[]> => ({
  data:
    data.data?.map((d: PurchasePlan) => ({
      ...d,
      offeredSecond: d.offered_second,
      promotionExpiredAt: d.promotion_expired_at,
    })) ?? [],
});

export interface OrderWithUIMeta {
  state: OrderState;
  errorCode?: string;
  error?: string;
}

export const orderData = (data: FetchedData<Order>): FetchedData<OrderWithUIMeta> => ({
  data: {
    ...data?.data,
    errorCode: data?.data?.error_code,
  },
});

export interface BusinessInfoWithUIMeta {
  organization: string;
  account: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  companyName?: string;
  taxId?: string;
}

export const businessInfo = (data: FetchedData<BusinessInfo>): FetchedData<BusinessInfoWithUIMeta> => ({
  data: {
    ...data?.data,
    firstName: data?.data?.first_name,
    lastName: data?.data?.last_name,
    companyName: data?.data?.company_name,
    taxId: data?.data?.tax_id,
  },
});

export const localeMap: Record<string, any> = {
  en: "en",
  zh: "zh-TW",
  ja: "ja",
};
